// ═══════════════════════════════════════════════════════════════════════════════
// CLOSING STRATEGY DASHBOARD v1.0
// EnergiPro Solutions — Structured Deal Closing & Conversion Interface
// ═══════════════════════════════════════════════════════════════════════════════
// Features: Trigger Actions, Status Management, Email Automation, Timing Rules,
//           Smart Alerts, Human Intervention Flags, Timeline View
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  Target, Phone, Mail, ArrowUpCircle, Trophy, XCircle, Clock,
  RefreshCw, Shield, ChevronDown, ChevronUp, Bell, AlertCircle,
  CheckCircle2, Handshake, Archive, Send, Eye, Zap, Timer,
  FileText, MessageSquare, UserCheck, TrendingUp, Flame, Snowflake, Sun
} from 'lucide-react';
import {
  ClosingStageRecord, ClosingStatus, ClosingEmailType, TriggerAction,
  CLOSING_STATUS_STYLES, TIMING_RULES,
  generateMockClosingData, generateClosingEmails, evaluateTrigger,
  requiresHumanIntervention,
} from '../../components/rfq/closingStrategyEngine';
import { TEMPERATURE_STYLES, DealTemperature } from '../../components/rfq/dealIntelligenceEngine';

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export const ClosingStrategy: React.FC = () => {
  const [deals, setDeals] = useState<ClosingStageRecord[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [notification, setNotification] = useState<string | null>(null);
  const [emailPreview, setEmailPreview] = useState<{ rfqId: string; type: ClosingEmailType } | null>(null);
  const [actionModal, setActionModal] = useState<{ rfqId: string; action: 'won' | 'lost' | 'negotiation' | 'awaiting' } | null>(null);
  const [actionNotes, setActionNotes] = useState('');

  useEffect(() => {
    setDeals(generateMockClosingData());
  }, []);

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 4000);
  };

  // ─── STATUS CHANGE ───────────────────────────────────────────────────────

  const handleStatusChange = (rfqId: string, newStatus: ClosingStatus) => {
    setDeals(prev => prev.map(d => {
      if (d.rfqId !== rfqId) return d;
      const entry = {
        id: `CS-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
        timestamp: new Date().toISOString(),
        action: `Status → ${CLOSING_STATUS_STYLES[newStatus].label}`,
        actor: 'Admin',
        details: actionNotes || undefined,
      };
      const intervention = requiresHumanIntervention(newStatus, d.temperature);
      return {
        ...d,
        closingStatus: newStatus,
        interventionRequired: intervention.required,
        interventionReason: intervention.reason,
        timeline: [...d.timeline, entry],
      };
    }));
    const labels: Record<string, string> = {
      won: '🏆 Deal marked as WON',
      lost: '✗ Deal marked as LOST',
      negotiation: '🤝 Moved to Negotiation',
      awaiting_confirmation: '⏳ Awaiting Confirmation',
    };
    showNotification(labels[newStatus] || `Status updated to ${newStatus}`);
    setActionModal(null);
    setActionNotes('');
  };

  // ─── SEND EMAIL ──────────────────────────────────────────────────────────

  const handleSendEmail = (rfqId: string, type: ClosingEmailType) => {
    setDeals(prev => prev.map(d => {
      if (d.rfqId !== rfqId) return d;
      const labels: Record<ClosingEmailType, string> = {
        execution_planning: 'Execution Planning Email Sent',
        clarification: 'Clarification Email Sent',
        reactivation: 'Reactivation Email Sent',
      };
      const entry = {
        id: `CS-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
        timestamp: new Date().toISOString(),
        action: labels[type],
        actor: 'Admin',
      };
      return { ...d, timeline: [...d.timeline, entry] };
    }));
    showNotification(`✓ Email sent successfully`);
    setEmailPreview(null);
  };

  // ─── FILTER & SORT ───────────────────────────────────────────────────────

  const filteredDeals = deals
    .filter(d => statusFilter === 'all' || d.closingStatus === statusFilter)
    .sort((a, b) => {
      // Hot first, then by health score
      const tempOrder: Record<DealTemperature, number> = { hot: 0, warm: 1, cold: 2 };
      const tempDiff = tempOrder[a.temperature] - tempOrder[b.temperature];
      if (tempDiff !== 0) return tempDiff;
      return b.dealHealthScore - a.dealHealthScore;
    });

  // ─── KPIs ─────────────────────────────────────────────────────────────────

  const total = deals.length;
  const inNegotiation = deals.filter(d => d.closingStatus === 'negotiation').length;
  const awaitingConf = deals.filter(d => d.closingStatus === 'awaiting_confirmation').length;
  const won = deals.filter(d => d.closingStatus === 'won').length;
  const lost = deals.filter(d => d.closingStatus === 'lost').length;
  const needsAction = deals.filter(d => d.interventionRequired).length;
  const hotActive = deals.filter(d => d.temperature === 'hot' && d.closingStatus === 'active').length;

  // ─── HELPERS ──────────────────────────────────────────────────────────────

  const formatDate = (isoDate: string | null): string => {
    if (!isoDate) return '—';
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTemperatureIcon = (temp: DealTemperature) => {
    switch (temp) {
      case 'hot': return <Flame className="w-3.5 h-3.5 text-red-400" />;
      case 'warm': return <Sun className="w-3.5 h-3.5 text-amber-400" />;
      case 'cold': return <Snowflake className="w-3.5 h-3.5 text-blue-400" />;
    }
  };

  const getUrgencyStyle = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'bg-red-500/15 border-red-500/40 text-red-300';
      case 'high': return 'bg-orange-500/15 border-orange-500/40 text-orange-300';
      case 'medium': return 'bg-amber-500/15 border-amber-500/40 text-amber-300';
      default: return 'bg-slate-500/15 border-slate-500/40 text-slate-300';
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-['Oswald'] text-3xl text-white uppercase tracking-wide flex items-center gap-3">
                <Target className="w-8 h-8 text-orange-400" />
                Closing Strategy
              </h1>
              <p className="text-slate-400 text-sm mt-1">Structured deal closing — convert proposals to confirmed jobs</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDeals(generateMockClosingData())}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:bg-slate-700 transition-all"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Closing</span>
              </div>
            </div>
          </div>

          {/* ═══ SMART ALERTS ═══ */}
          {hotActive > 0 && (
            <div className="mb-4 bg-gradient-to-r from-red-900/30 to-orange-900/20 border border-red-500/40 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Bell className="w-5 h-5 text-red-400 animate-bounce" />
                </div>
                <div>
                  <h3 className="text-red-300 font-bold text-sm uppercase tracking-wider">
                    🔥 {hotActive} Hot Deal{hotActive > 1 ? 's' : ''} — Immediate Action Required
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">
                    High probability deals detected. Call within 24 hours to maximize conversion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {deals.filter(d => d.temperature === 'cold' && d.daysWithoutResponse >= 14).length > 0 && (
            <div className="mb-4 bg-gradient-to-r from-blue-900/20 to-slate-900/20 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-blue-300 font-bold text-sm uppercase tracking-wider">
                    ❄️ At Risk — Consider Closing
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {deals.filter(d => d.temperature === 'cold' && d.daysWithoutResponse >= 14).length} deal(s) with 14+ days inactivity. Recommend close or final reactivation attempt.
                  </p>
                </div>
              </div>
            </div>
          )}

          {needsAction > 0 && (
            <div className="mb-6 bg-gradient-to-r from-purple-900/20 to-slate-900/20 border border-purple-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <UserCheck className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-purple-300 font-bold text-sm uppercase tracking-wider">
                    🤝 {needsAction} Deal{needsAction > 1 ? 's' : ''} Require Human Intervention
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">
                    Negotiation/Contract stages require manual action — automated emails paused.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ═══ KPI STRIP ═══ */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
            <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-white">{total}</div>
              <div className="text-slate-500 text-[10px] mt-0.5">Pipeline</div>
            </div>
            <div className="bg-[#111827] border border-purple-500/30 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-purple-400">{inNegotiation}</div>
              <div className="text-slate-500 text-[10px] mt-0.5">Negotiation</div>
            </div>
            <div className="bg-[#111827] border border-amber-500/30 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-amber-400">{awaitingConf}</div>
              <div className="text-slate-500 text-[10px] mt-0.5">Awaiting</div>
            </div>
            <div className="bg-[#111827] border border-emerald-500/30 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-emerald-400">{won}</div>
              <div className="text-slate-500 text-[10px] mt-0.5">Won</div>
            </div>
            <div className="bg-[#111827] border border-red-500/30 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-red-400">{lost}</div>
              <div className="text-slate-500 text-[10px] mt-0.5">Lost</div>
            </div>
            <div className="bg-[#111827] border border-orange-500/30 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-orange-400">{needsAction}</div>
              <div className="text-slate-500 text-[10px] mt-0.5">Needs Action</div>
            </div>
          </div>

          {/* ═══ TIMING RULES REFERENCE ═══ */}
          <div className="mb-6 bg-[#111827] border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Timer className="w-4 h-4 text-orange-400" />
              <h3 className="text-slate-300 text-xs font-bold uppercase tracking-wider">Timing Rules</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {TIMING_RULES.map(rule => (
                <div key={rule.id} className={`px-3 py-2 rounded-lg border text-xs ${getUrgencyStyle(rule.urgency)}`}>
                  <div className="font-medium">{rule.action}</div>
                  <div className="text-slate-500 mt-0.5">{rule.condition} • {rule.threshold}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══ FILTER BAR ═══ */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <span className="text-slate-400 text-sm">Status:</span>
            {(['all', 'active', 'negotiation', 'awaiting_confirmation', 'won', 'lost'] as const).map(s => {
              const isActive = statusFilter === s;
              const style = s === 'all'
                ? { bg: 'bg-slate-700/50', text: 'text-slate-300', border: 'border-slate-600' }
                : CLOSING_STATUS_STYLES[s];
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    isActive
                      ? `${style.bg} ${style.text} ${style.border} ring-1 ring-offset-0`
                      : 'bg-slate-800/50 text-slate-500 border-slate-700 hover:text-slate-300'
                  }`}
                >
                  {s === 'all' ? 'All' : CLOSING_STATUS_STYLES[s].label}
                </button>
              );
            })}
          </div>

          {/* ═══ DEAL CARDS ═══ */}
          <div className="space-y-4">
            {filteredDeals.map(deal => {
              const isExpanded = expandedId === deal.rfqId;
              const statusStyle = CLOSING_STATUS_STYLES[deal.closingStatus];
              const tempStyle = TEMPERATURE_STYLES[deal.temperature];

              return (
                <div
                  key={deal.rfqId}
                  className={`bg-[#111827] border rounded-xl overflow-hidden transition-all ${
                    deal.interventionRequired
                      ? 'border-purple-500/40 ring-1 ring-purple-500/15'
                      : deal.temperature === 'hot'
                      ? 'border-red-500/30'
                      : 'border-slate-700/50'
                  }`}
                >
                  {/* Card Header */}
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-800/30 transition-all"
                    onClick={() => setExpandedId(isExpanded ? null : deal.rfqId)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Status Badge */}
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${statusStyle.bg} ${statusStyle.border}`}>
                          <span className="text-sm">{statusStyle.icon}</span>
                          <span className={`text-[10px] font-bold uppercase ${statusStyle.text}`}>{statusStyle.label}</span>
                        </div>

                        {/* Temperature */}
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-md border ${tempStyle.bg} ${tempStyle.border}`}>
                          {getTemperatureIcon(deal.temperature)}
                          <span className={`text-[10px] font-bold ${tempStyle.text}`}>{tempStyle.label}</span>
                        </div>

                        {/* Intervention Flag */}
                        {deal.interventionRequired && (
                          <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/30">
                            <UserCheck className="w-3 h-3 text-purple-400" />
                            <span className="text-[10px] font-bold text-purple-300">MANUAL</span>
                          </div>
                        )}

                        {/* Client Info */}
                        <div className="ml-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold text-sm">{deal.clientName}</span>
                            <span className="text-slate-500 text-xs">• {deal.companyName}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-slate-400 text-xs">{deal.referenceId}</span>
                            <span className="text-slate-600">|</span>
                            <span className="text-slate-400 text-xs">{deal.serviceName}</span>
                            {deal.proposalValue && (
                              <>
                                <span className="text-slate-600">|</span>
                                <span className="text-emerald-400 text-xs font-medium">{deal.proposalValue}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Trigger Action Badge */}
                        <div className={`hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${getUrgencyStyle(deal.triggeredAction.urgency)}`}>
                          <Zap className="w-3 h-3" />
                          <span className="text-[10px] font-medium">{deal.triggeredAction.label}</span>
                        </div>

                        {/* Days Counter */}
                        <div className="text-right">
                          <div className={`text-lg font-bold ${deal.daysWithoutResponse >= 7 ? 'text-red-400' : deal.daysWithoutResponse >= 3 ? 'text-amber-400' : 'text-green-400'}`}>
                            {deal.daysWithoutResponse}d
                          </div>
                          <div className="text-slate-600 text-[9px]">Silent</div>
                        </div>

                        {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                      </div>
                    </div>
                  </div>

                  {/* ═══ EXPANDED SECTION ═══ */}
                  {isExpanded && (
                    <div className="border-t border-slate-700/50 p-5 bg-slate-900/30">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* Column 1: Actions & Status Change */}
                        <div>
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Target className="w-3.5 h-3.5 text-orange-400" /> Dashboard Actions
                          </h4>
                          <div className="grid grid-cols-1 gap-2 mb-4">
                            <button
                              onClick={(e) => { e.stopPropagation(); setActionModal({ rfqId: deal.rfqId, action: 'negotiation' }); }}
                              className="flex items-center gap-2 px-3 py-2.5 bg-purple-500/10 border border-purple-500/30 rounded-lg text-purple-300 text-xs font-medium hover:bg-purple-500/20 transition-all"
                            >
                              <Handshake className="w-4 h-4" /> Mark as Negotiation
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setActionModal({ rfqId: deal.rfqId, action: 'awaiting' }); }}
                              className="flex items-center gap-2 px-3 py-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs font-medium hover:bg-amber-500/20 transition-all"
                            >
                              <Clock className="w-4 h-4" /> Awaiting Confirmation
                            </button>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setActionModal({ rfqId: deal.rfqId, action: 'won' }); }}
                                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-300 text-xs font-medium hover:bg-emerald-500/20 transition-all"
                              >
                                <Trophy className="w-4 h-4" /> Won
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setActionModal({ rfqId: deal.rfqId, action: 'lost' }); }}
                                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-xs font-medium hover:bg-red-500/20 transition-all"
                              >
                                <XCircle className="w-4 h-4" /> Lost
                              </button>
                            </div>
                          </div>

                          {/* Quick Actions */}
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-2 mt-4 flex items-center gap-2">
                            <Zap className="w-3.5 h-3.5 text-yellow-400" /> Quick Actions
                          </h4>
                          <div className="space-y-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); showNotification('📞 Call logged for ' + deal.clientName); }}
                              className="w-full flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-xs hover:bg-green-500/20 transition-all"
                            >
                              <Phone className="w-3.5 h-3.5" /> Call Client
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); showNotification('⬆️ Escalation sent'); }}
                              className="w-full flex items-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-300 text-xs hover:bg-orange-500/20 transition-all"
                            >
                              <ArrowUpCircle className="w-3.5 h-3.5" /> Send Escalation Email
                            </button>
                          </div>

                          {/* Intervention Notice */}
                          {deal.interventionRequired && (
                            <div className="mt-4 p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <UserCheck className="w-3.5 h-3.5 text-purple-400" />
                                <span className="text-purple-300 text-xs font-bold">Human Intervention Required</span>
                              </div>
                              <p className="text-slate-400 text-[10px]">{deal.interventionReason}</p>
                            </div>
                          )}
                        </div>

                        {/* Column 2: Email Automation */}
                        <div>
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-cyan-400" /> Closing Emails
                          </h4>
                          <div className="space-y-2">
                            {deal.availableEmails.map((email, idx) => (
                              <div key={idx} className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-slate-300 text-xs font-medium">{email.label}</span>
                                  <div className="flex gap-1">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setEmailPreview({ rfqId: deal.rfqId, type: email.type }); }}
                                      className="p-1 bg-slate-700/50 rounded text-slate-400 hover:text-slate-200 transition-colors"
                                    >
                                      <Eye className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleSendEmail(deal.rfqId, email.type); }}
                                      className="p-1 bg-cyan-500/20 rounded text-cyan-400 hover:text-cyan-200 transition-colors"
                                    >
                                      <Send className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                <p className="text-slate-500 text-[10px]">{email.description}</p>
                                <div className="mt-1 text-[9px] text-slate-600">Trigger: {email.triggerCondition}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Column 3: Timeline & Notes */}
                        <div>
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-green-400" /> Deal Timeline
                          </h4>
                          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-2">
                            {deal.timeline
                              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                              .map((entry, idx) => (
                                <div key={idx} className="flex items-start gap-2 px-2.5 py-2 bg-slate-800/30 rounded-lg border border-slate-700/20">
                                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${
                                    entry.actor === 'Client' ? 'bg-cyan-400' : entry.actor === 'System' ? 'bg-slate-400' : 'bg-orange-400'
                                  }`} />
                                  <div className="flex-1">
                                    <div className="text-[11px] text-slate-300">{entry.action}</div>
                                    {entry.details && <div className="text-[9px] text-slate-500">{entry.details}</div>}
                                    <div className="text-[9px] text-slate-600">{formatDate(entry.timestamp)} • {entry.actor}</div>
                                  </div>
                                </div>
                              ))}
                          </div>

                          {/* Notes */}
                          {deal.notes.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-slate-500 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" /> Notes
                              </h5>
                              <div className="space-y-1">
                                {deal.notes.map((note, idx) => (
                                  <div key={idx} className="text-[11px] text-slate-400 px-2 py-1 bg-slate-800/20 rounded border-l-2 border-orange-500/30">
                                    {note}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredDeals.length === 0 && (
            <div className="text-center py-16">
              <Target className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500 text-sm">No deals match the selected filter.</p>
            </div>
          )}
        </div>
      </div>

      {/* ═══ STATUS CHANGE MODAL ═══ */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setActionModal(null)}>
          <div className="bg-[#111827] border border-slate-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-1">
              {actionModal.action === 'won' && '🏆 Mark Deal as Won'}
              {actionModal.action === 'lost' && '✗ Mark Deal as Lost'}
              {actionModal.action === 'negotiation' && '🤝 Move to Negotiation'}
              {actionModal.action === 'awaiting' && '⏳ Awaiting Confirmation'}
            </h3>
            <p className="text-slate-400 text-xs mb-4">
              {actionModal.action === 'won' && 'Congratulations! Record the win details and any relevant notes.'}
              {actionModal.action === 'lost' && 'Record the reason for loss to improve future conversion.'}
              {actionModal.action === 'negotiation' && 'Deal enters manual negotiation — automated emails will pause.'}
              {actionModal.action === 'awaiting' && 'Client has verbally confirmed — awaiting formal PO/contract.'}
            </p>
            <textarea
              value={actionNotes}
              onChange={e => setActionNotes(e.target.value)}
              placeholder={
                actionModal.action === 'won' ? 'PO number, contract value, next steps...'
                  : actionModal.action === 'lost' ? 'Reason for loss (price, timing, competitor, scope)...'
                  : actionModal.action === 'negotiation' ? 'Key negotiation points, client requirements...'
                  : 'Expected confirmation timeline...'
              }
              className="w-full h-24 px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-orange-500 resize-none"
            />
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => {
                  const statusMap: Record<string, ClosingStatus> = {
                    won: 'won', lost: 'lost', negotiation: 'negotiation', awaiting: 'awaiting_confirmation'
                  };
                  handleStatusChange(actionModal.rfqId, statusMap[actionModal.action]);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 rounded-lg text-white text-sm font-medium hover:bg-orange-700 transition-all"
              >
                <CheckCircle2 className="w-4 h-4" /> Confirm
              </button>
              <button
                onClick={() => setActionModal(null)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 rounded-lg text-slate-300 text-sm font-medium hover:bg-slate-600 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ EMAIL PREVIEW MODAL ═══ */}
      {emailPreview && (() => {
        const deal = deals.find(d => d.rfqId === emailPreview.rfqId);
        const email = deal?.availableEmails.find(e => e.type === emailPreview.type);
        if (!deal || !email) return null;
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEmailPreview(null)}>
            <div className="bg-[#111827] border border-slate-700 rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-cyan-400" /> {email.label}
                </h3>
                <button onClick={() => setEmailPreview(null)} className="text-slate-500 hover:text-slate-300">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-3">
                <div className="text-xs text-slate-500">To: <span className="text-slate-300">{deal.clientEmail}</span></div>
                <div className="text-xs text-slate-500">Subject: <span className="text-slate-300">{email.subject}</span></div>
              </div>
              <div className="bg-slate-900 border border-slate-700/50 rounded-lg p-4">
                <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{email.body}</pre>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={() => handleSendEmail(deal.rfqId, emailPreview.type)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-600 rounded-lg text-white text-sm font-medium hover:bg-cyan-700 transition-all"
                >
                  <Send className="w-4 h-4" /> Send Email
                </button>
                <button
                  onClick={() => setEmailPreview(null)}
                  className="px-4 py-2.5 bg-slate-700 rounded-lg text-slate-300 text-sm font-medium hover:bg-slate-600 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ NOTIFICATION TOAST ═══ */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div className="bg-green-900/90 border border-green-500/40 rounded-xl px-5 py-3 text-green-300 text-sm font-medium shadow-2xl backdrop-blur-sm">
            {notification}
          </div>
        </div>
      )}
    </Layout>
  );
};
